import React from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { 
  X, 
  LogOut, 
  Info, 
  PlusCircle, 
  MessageSquare 
} from 'lucide-react';

/**
 * Mock list of past conversation items for layout demonstration.
 * @type {Array<{id: string, title: string, date: string}>}
 */
const MOCK_CONVERSATIONS = [
  { id: '1', title: 'עיבוד קבלות מאי 2026', date: 'היום' },
  { id: '2', title: 'חילוץ נתונים מדוח חודשי', date: 'אתמול' },
  { id: '3', title: 'בדיקת תאימות חשבונית ספק', date: '14/08/2026' },
  { id: '4', title: 'סיווג הוצאות משרדיות', date: '10/08/2026' }
];

/**
 * Sidebar component dedicated to chat management and past conversations history.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Controls the visibility state of the sidebar drawer.
 * @param {() => void} props.onClose - Callback triggered to close the sidebar.
 * @param {Object|null} [props.user] - Authenticated user profile information.
 * @param {string} [props.user.name] - User full name.
 * @param {string} [props.user.email] - User email address.
 * @param {string} [props.user.picture] - User avatar URL.
 * @param {() => void} props.handleLogout - Callback triggered to initiate user sign-out.
 * @param {() => void} [props.onNewChat] - Optional callback triggered when starting a new chat session.
 * @param {string} [props.activeChatId] - Optional ID of the currently selected conversation.
 * @param {(chatId: string) => void} [props.onSelectChat] - Optional callback triggered on chat item click.
 * @returns {JSX.Element} The rendered Sidebar component.
 */
export default function Sidebar({
  isOpen,
  onClose,
  user,
  handleLogout,
  onNewChat,
  activeChatId = '1',
  onSelectChat
}) {
  /**
   * Handles initiating a new chat session.
   */
  const handleNewChatClick = () => {
    console.log('[Sidebar] Initiating new chat session');
    if (onNewChat) {
      onNewChat();
    }
    if (onClose) {
      onClose();
    }
  };

  /**
   * Handles selecting an existing chat from history.
   *
   * @param {string} chatId - Selected conversation unique identifier.
   */
  const handleChatSelect = (chatId) => {
    console.log(`[Sidebar] Selected conversation ID: ${chatId}`);
    if (onSelectChat) {
      onSelectChat(chatId);
    }
    if (onClose) {
      onClose();
    }
  };

  /**
   * Handles user logout sequence safely.
   */
  const handleLogoutClick = () => {
    console.log('[Sidebar] Executing logout trigger for user:', user?.email || 'Guest');
    if (onClose) {
      onClose();
    }
    handleLogout();
  };

  return (
    <>
      {/* Darkened overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Main Drawer Container */}
      <aside
        className={`fixed top-0 right-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out p-6 flex flex-col border-l border-slate-100 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header & Close Button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-slate-900 text-lg">תפריט שיחות</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="סגור תפריט"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Action Button: New Chat */}
        <button
          onClick={handleNewChatClick}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-xl shadow-sm transition-colors mb-6"
        >
          <PlusCircle className="w-4 h-4" />
          <span>שיחה חדשה</span>
        </button>

        {/* Scrollable Past Conversations Section (Mock Data) */}
        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          <span className="px-2 text-xs font-semibold text-slate-400 tracking-wider block mb-2">
            שיחות אחרונות
          </span>
          <div className="space-y-1">
            {MOCK_CONVERSATIONS.map((chat) => {
              const isSelected = chat.id === activeChatId;
              return (
                <button
                  key={chat.id}
                  onClick={() => handleChatSelect(chat.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-xs rounded-lg text-right transition-colors ${
                    isSelected
                      ? 'bg-slate-100 text-slate-900 font-semibold'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{chat.title}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0 mr-2">
                    {chat.date}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer Section */}
        <div className="border-t border-slate-100 pt-4 mt-auto space-y-4">
          {/* User Profile Info */}
          {user && (
            <div
              className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100 text-right"
              dir="rtl"
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || 'פרופיל משתמש'}
                  className="w-8 h-8 rounded-full border border-slate-200 object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">
                  {user.name?.[0] || 'U'}
                </div>
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-xs font-semibold text-slate-800 truncate">
                  {user.name}
                </span>
                <span className="text-[10px] text-slate-400 truncate">
                  {user.email}
                </span>
              </div>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-right transition-colors text-rose-600 hover:bg-rose-50 hover:text-rose-700"
          >
            <LogOut className="w-4 h-4" />
            <span>התנתקות מהמערכת</span>
          </button>

          {/* Legal Links */}
          <div className="flex items-center justify-center gap-3 text-[11px] text-slate-400 border-t border-slate-100 pt-3">
            <NavLink
              to="/privacy"
              onClick={onClose}
              className="hover:text-indigo-600 transition-colors underline"
            >
              מדיניות פרטיות
            </NavLink>
            <span className="text-slate-200">•</span>
            <NavLink
              to="/terms"
              onClick={onClose}
              className="hover:text-indigo-600 transition-colors underline"
            >
              תנאי שימוש
            </NavLink>
          </div>

          {/* System Version Indicator */}
          <div className="flex items-center gap-2 text-[11px] text-slate-400 justify-center bg-slate-50/50 py-1.5 rounded-lg border border-slate-100/50">
            <Info className="w-3.5 h-3.5 text-slate-400" />
            <span>גרסה 1.0.1 (Localhost)</span>
          </div>
        </div>
      </aside>
    </>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    picture: PropTypes.string
  }),
  handleLogout: PropTypes.func.isRequired,
  onNewChat: PropTypes.func,
  activeChatId: PropTypes.string,
  onSelectChat: PropTypes.func
};